import React, { useEffect, useMemo, useRef } from 'react';
import { Select } from "antd";

/**
 * Fabric pattern field.
 *
 * A dropdown like every other select on this row, but a searchable one carrying
 * the cloth: the book runs to hundreds of prints for a single fabric, and
 * "print-142" tells the operator nothing on its own. So each option is a swatch
 * beside its name, and the chosen one keeps its swatch in the closed control.
 *
 * Rendered under a Form.Item, so `value`/`onChange` arrive as props. `value` is
 * the pattern id; `patterns` is the list for the line's own fabric — a pattern
 * belongs to one fabric, and the API rejects a line that pairs them wrongly.
 */
const swatch = (url) => (
    <span
        style={{
            width: 26,
            height: 18,
            borderRadius: 3,
            flex: "none",
            overflow: "hidden",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            display: "inline-flex",
        }}
    >
        {url && (
            <img src={url} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        )}
    </span>
);

const PatternPicker = ({
    value,
    onChange,
    patterns = [],
    loading = false,
    fabricTypeId = null,
    onOpen,
}) => {
    // Changing or clearing the fabric leaves a pattern that no longer belongs to
    // anything; dropping it here keeps the form from submitting a pair the API
    // rejects.
    //
    // A *transition* away from a fabric, emphatically not the mere absence of
    // one. The row's fabric arrives through `Form.useWatch`, which reports
    // `undefined` on its first render and the real value only after its own
    // effect has run — so on mount an edited order's line looks, for one render,
    // exactly like a line whose fabric has just been cleared. Clearing on that
    // wiped the pattern off the one line the form opens expanded (the others are
    // collapsed and never mount a picker), and the wipe was then saved by the
    // next update. Remembering the previous fabric makes null -> id, which is
    // what hydration looks like, a non-event.
    const previousFabric = useRef(fabricTypeId);
    useEffect(() => {
        const had = previousFabric.current;
        previousFabric.current = fabricTypeId;

        if (had && had !== fabricTypeId && value) onChange?.(null);
    }, [fabricTypeId, value, onChange]);

    const options = useMemo(
        () => patterns.map((p) => ({
            value: p.id,
            // Kept alongside the node so the search filters on text, not markup.
            name: p.name,
            label: (
                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    {swatch(p.url)}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.name}
                    </span>
                </span>
            ),
        })),
        [patterns],
    );

    return (
        <Select
            showSearch
            allowClear
            disabled={!fabricTypeId}
            loading={loading}
            value={value ?? undefined}
            onChange={(next) => onChange?.(next ?? null)}
            placeholder={fabricTypeId ? 'Search pattern' : 'Select fabric type first'}
            // The book is fetched when the fabric is chosen; asking again here
            // covers a line whose fabric arrived with an edited order.
            onDropdownVisibleChange={(open) => { if (open && fabricTypeId) onOpen?.(fabricTypeId); }}
            filterOption={(input, option) =>
                (option?.name || '').toLowerCase().includes(input.trim().toLowerCase())}
            // Two different empty states. A fabric with no print book is not a
            // failed search — saying "no pattern matches that" against a list
            // that was never going to have any reads like the search is broken.
            notFoundContent={loading
                ? 'Loading…'
                : (patterns.length ? 'No pattern matches that' : 'No patterns for this fabric')}
            options={options}
        />
    );
};

export default PatternPicker;
